import React, { useState, useEffect, FC } from "react";
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Modal,
  ModalBody,
  UncontrolledDropdown,
  Badge,
  Col,
  Row,
  Alert,
  Table,
  Button as ReactstrapButton,
} from "reactstrap";
import { Icon, Button, TooltipComponent } from "../../../components/Component";
import brandMasterApis from "../../../api/master/brandMaster";
import { useSelector } from "react-redux";
import TransactionFlowDefaultTemplate from "../../others/transactionFlowDefaultTemplate";
import dayjs from "dayjs";
import BrandMasterForm from "./BrandMasterForm";
import FormatDate from "../DateFormater";
import { Select } from "antd";
import { Option } from "antd/es/mentions";
import { normalizeStatus } from "../../../utils/Utils";

interface Brand {
  id: number;
  make: string;
  status: "Active" | "InActive";
  createdDate: string;
  modifiedDate: string;
}

const BrandMaster: FC = () => {
  const [refreshToggle, setRefreshToggle] = useState(false);
  const { data: accessibility } = useSelector(
    (state: any) => state.accessibility
  );
  const [allowedActions, setAllowedActions] = useState<any>({
    add: 0,
    view: 0,
    edit: 0,
    approve: 0,
    reject: 0,
    cancel: 0,
  });

  // Modal states
  const [isOpen, setIsOpen] = useState(false);
  const [viewModal, setViewModal] = useState(false);
  const [detail, setDetail] = useState<any>({});

  const [popUpContent, setPopUpContent] = useState<JSX.Element | null>(null);
  const [isEditModeInView, setIsEditModeInView] = useState(false);
  const [loading, setLoading] = useState(false);
  const [alertInfo, setAlertInfo] = useState({
    type: "",
    message: "",
    errors: [],
  });

  useEffect(() => {
    if (alertInfo.message) {
      const timer = setTimeout(() => {
        setAlertInfo({ type: "", message: "", errors: [] });
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [alertInfo.message]);

  useEffect(() => {
    if (accessibility) {
      try {
        const { add, view, edit, approve, reject, cancel } =
          accessibility["Master"];
        setAllowedActions({ add, view, edit, approve, reject, cancel });
      } catch (error) {
        console.log(error);
      }
    }
  }, [accessibility]);

  const refreshData = () => {
    setRefreshToggle((prev) => !prev);
  };

  const loadDetail = async (materialId: any) => {
    try {
      const response = await brandMasterApis.fetchBrandById(materialId);
      if (response && response.status) {
        setDetail(response.data);
      } else {
        console.error("Failed to fetch material details");
        setDetail({ materialId });
      }
    } catch (error) {
      console.error("Error fetching material details:", error);
      setDetail({ materialId });
    }
  };

  const handleModelPopUp = (id?: any, type?: string) => {
    const content = (
      <BrandMasterForm
        formAction={type}
        id={id}
        toggleModelPopUp={() => {
          setIsOpen(false);
        }}
        refreshData={refreshData}
      />
    );
    setPopUpContent(content);
    setIsOpen(true);
  };

  const handleViewModalClose = () => {
    setViewModal(false);
    setIsEditModeInView(false);
    setAlertInfo({ type: "", message: "", errors: [] });
  };

  const handleEditInView = () => {
    setIsEditModeInView(true);
  };

  const handleCancelEditInView = () => {
    setIsEditModeInView(false);
    if (detail.materialId) {
      loadDetail(detail.materialId);
    }
  };

  const handleStatusChange = async (
    brandId: number,
    newStatus: "Active" | "InActive"
  ) => {
    try {
      setLoading(true);

      let response;
      if (newStatus === "Active") {
        response = await brandMasterApis.allowActiveStatus(brandId);
      } else {
        response = await brandMasterApis.removeActiveStatus(brandId);
      }

      if (response.status) {
        setAlertInfo({
          type: "success",
          message: `Brand has been ${
            newStatus === "Active" ? "Activated" : "inActivated"
          }`,
          errors: [],
        });

        refreshData();
        if (detail.materialId) {
          await loadDetail(detail.materialId);
        }
      } else {
        setAlertInfo({
          type: "error",
          message: response.message,
          errors: [],
        });
      }
    } catch (error: any) {
      setAlertInfo({
        type: "error",
        message:
          error.response?.data?.message ||
          "An error occurred while updating status",
        errors: [],
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBrand = async (brandId: number) => {
    try {
      const response = await brandMasterApis.deleteBrandById(brandId);
      if (response.status) {
        setAlertInfo({
          type: "success",
          message: "Brand has been deleted",
          errors: [],
        });

        refreshData();
        if (detail.materialId) {
          await loadDetail(detail.materialId);
        }
      } else {
        setAlertInfo({
          type: "error",
          message: response.message,
          errors: [],
        });
      }
    } catch (error: any) {
      setAlertInfo({
        type: "error",
        message: error.message,
        errors: [],
      });
    }
  };

  const columns = [
    {
      title: "Category",
      dataIndex: "categoryName",
      key: "categoryName",
      width: 150,
    },
    {
      title: "Material Name",
      dataIndex: "productName",
      key: "productName",
      width: 150,
    },
    {
      title: "Make",
      dataIndex: "brands",
      key: "brands",
      width: 200,
      render: (brands: any[], record: any, index: number) => {
        if (!brands || brands.length === 0) return "N/A";

        const makes = brands.map((brand) => brand.make).filter(Boolean);
        const makesText = makes.join(", ");

        return (
          <div className="d-flex align-items-center">
            <span id={`makes-tooltip-${index}`}>
              {makesText.length > 30
                ? `${makesText.substring(0, 30)}...`
                : makesText}
            </span>

            {makesText.length > 30 && (
              <TooltipComponent
                iconClass="info-circle text-info ms-1"
                icon="info"
                id={`makes-tooltip-${index}`}
                direction="top"
                text={makesText}
                containerClassName=""
              />
            )}
          </div>
        );
      },
    },
    {
      title: "Created Date",
      dataIndex: "createdDate",
      key: "createdDate",
      width: 150,
      render: (text: any) => <span>{dayjs(text).format("DD-MMM-YYYY")}</span>,
    },
    {
      title: "Created By",
      dataIndex: "createdByUserName",
      key: "createdByUserName",
      width: 150,
      render: (text: any) => text || "N/A",
    },
    {
      title: "Details",
      dataIndex: "action",
      key: "action",
      width: 90,
      render: (text: any, record: any) => {
        return (
          <div style={{ display: "flex" }}>
            <ul className="nk-tb-actions">
              <li
                className="nk-tb-actions-toggle"
                onClick={async () => {
                  await loadDetail(record.materialId);
                  setViewModal(true);
                }}
              >
                <TooltipComponent
                  tag="a"
                  containerClassName="btn btn-trigger btn-icon"
                  id={"view" + record.materialId}
                  icon="focus"
                  direction="top"
                  text="View Details"
                  iconClass={undefined}
                />
              </li>
            </ul>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <TransactionFlowDefaultTemplate
        moduleKey="brandmaster-list"
        title="Brand Master"
        columns={columns}
        extra={{
          tableScrollX: 1200,
          excelExport: true,
        }}
        refresh={refreshToggle}
        topRightJSX={
          allowedActions?.add ? (
            <Button
              color="primary"
              onClick={() => handleModelPopUp(null, "add")}
            >
              <Icon name="plus"></Icon>
              <span>Add</span>
            </Button>
          ) : (
            <></>
          )
        }
      />

      {/* Add/Edit Modal */}
      <Modal
        isOpen={isOpen}
        toggle={() => setIsOpen(false)}
        className="modal-dialog-centered"
        size="lg"
        backdrop="static"
      >
        <a
          href="#cancel"
          onClick={(ev) => {
            ev.preventDefault();
            setIsOpen(false);
          }}
          className="close"
        >
          <Icon name="cross-sm"></Icon>
        </a>
        <ModalBody>{popUpContent}</ModalBody>
      </Modal>

      {/* View Details Modal with Three-Dot Menu */}
      <Modal
        isOpen={viewModal}
        toggle={handleViewModalClose}
        className="modal-dialog-centered"
        size="lg"
        backdrop="static"
      >
        <ModalBody>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 className="nk-modal-title title mb-0">
              {isEditModeInView ? "Edit Brand Details" : "View Brand Details"}
            </h4>
            <div className="d-flex align-items-center gap-2">
              {isEditModeInView ? (
                <Button
                  color="light"
                  className="border"
                  onClick={handleCancelEditInView}
                >
                  <Icon name="x" className="me-1" /> Cancel
                </Button>
              ) : (
                <UncontrolledDropdown>
                  <DropdownToggle
                    tag="a"
                    className="dropdown-toggle btn btn-icon btn-trigger me-2"
                  >
                    <Icon name="more-v" />
                  </DropdownToggle>
                  <DropdownMenu end>
                    <DropdownItem
                      onClick={(e) => {
                        e.preventDefault();
                        setIsEditModeInView(true);
                      }}
                    >
                      <Icon name="edit" /> Edit
                    </DropdownItem>
                  </DropdownMenu>
                </UncontrolledDropdown>
              )}
              <a
                href="#close"
                onClick={(ev) => {
                  ev.preventDefault();
                  handleViewModalClose();
                }}
                className="btn btn-icon"
                style={{ height: "15px", fontSize: "1.6rem" }}
              >
                <Icon name="cross-sm" />
              </a>
            </div>
          </div>

          {alertInfo.message && (
            <div className="mb-3">
              <Alert
                color={alertInfo.type === "error" ? "danger" : "success"}
                className="alert-icon"
              >
                <Icon
                  name={
                    alertInfo.type === "error" ? "alert-circle" : "check-circle"
                  }
                />
                <strong>{alertInfo.message}</strong>
              </Alert>
            </div>
          )}

          <div className="nk-tnx-details mt-sm-3">
            <Row className="gy-3">
              <Col lg={6}>
                <span className="sub-text">Category</span>
                <span className="caption-text">{detail.categoryName}</span>
              </Col>
              <Col lg={6}>
                <span className="sub-text">Material Name</span>
                <span className="caption-text">{detail.productName}</span>
              </Col>

              <Col lg={6}>
                <span className="sub-text">Created Date</span>
                <span className="caption-text">
                  {detail.createdAt ? FormatDate(detail.createdAt) : "N/A"}
                </span>
              </Col>
              <Col lg={6}>
                <span className="sub-text">Created By</span>
                <span className="caption-text">
                  {detail.createdByUserName || "N/A"}
                </span>
              </Col>

              <Col lg={12}>
                <span className="sub-text">Brands</span>
                <div className="caption-text">
                  {detail.brands && detail.brands.length > 0 ? (
                    <div>
                      <Table
                        striped
                        responsive
                        className="brand-table"
                        style={{ fontSize: "14px", borderCollapse: "separate" }}
                      >
                        <thead>
                          <tr>
                            {/* <th>ID</th> */}
                            <th style={{ textAlign: "center", width: "150px" }}>
                              Make
                            </th>
                            <th style={{ textAlign: "center", width: "150px" }}>
                              Status
                            </th>
                            {isEditModeInView && (
                              <th
                                style={{ textAlign: "center", width: "150px" }}
                              >
                                Actions
                              </th>
                            )}
                          </tr>
                        </thead>

                        <tbody>
                          {detail.brands.map((brand: Brand) => {
                            const normalizedStatus = normalizeStatus(
                              brand.status
                            );
                            return (
                              <tr key={brand.id}>
                                {/* Brand Make */}
                                <td className="align-middle text-center">
                                  <span className="fw-medium">
                                    {brand.make}
                                  </span>
                                </td>

                                {/* Status column */}
                                <td className="align-middle text-center">
                                  {isEditModeInView ? (
                                    <Select
                                      value={normalizedStatus}
                                      onChange={(
                                        value: "Active" | "InActive"
                                      ) => handleStatusChange(brand.id, value)}
                                      style={{
                                        width: 120,
                                        fontWeight: 600,
                                        color:
                                          normalizedStatus === "Active"
                                            ? "#2ECC71"
                                            : "#E74C3C",
                                      }}
                                    >
                                      <Option value="Active">Active</Option>
                                      <Option value="InActive">Inactive</Option>
                                    </Select>
                                  ) : (
                                    <span
                                      style={{
                                        color:
                                          normalizedStatus === "Active"
                                            ? "#2ECC71"
                                            : "#E74C3C",
                                        fontWeight: 600,
                                        fontSize: 14,
                                      }}
                                    >
                                      {normalizedStatus}
                                    </span>
                                  )}
                                </td>

                                {/* Delete Action */}
                                {isEditModeInView && (
                                  <td className="align-middle text-center">
                                    <div className="d-flex justify-content-center align-items-center gap-3">
                                      <ReactstrapButton
                                        color="link"
                                        className="btn-icon p-0 border-0"
                                        onClick={() =>
                                          handleDeleteBrand(brand.id)
                                        }
                                        disabled={loading}
                                        title="Delete Brand"
                                      >
                                        <Icon
                                          name="trash"
                                          className="text-danger"
                                          style={{ fontSize: "22px" }}
                                        />
                                      </ReactstrapButton>
                                    </div>
                                  </td>
                                )}
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </div>
                  ) : (
                    <Alert color="info">
                      No brands found for this material.
                    </Alert>
                  )}
                </div>
              </Col>
            </Row>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
};

export default BrandMaster;
